import { addDays } from 'date-fns';
import { useMemo } from 'react';
import { formatTravelDateRangeLabel } from '../../../../../../utils/formatTravelDateRange';
import {
  CmsCountry,
  CmsRegion,
  ComponentTravelPolicySearch,
  TravelInsuranceType,
} from '../../../../../app/[site]/[locale]/(cms)/_modules/cmsService/types';
import {
  MAX_TRAVELLERS,
  MIN_ADULTS,
  MIN_CHILDREN,
} from '../../../../../app/[site]/[locale]/(cms)/_modules/travelQuotesService/constants';
import { normalizeTravelParams } from '../../../../../app/[site]/[locale]/(cms)/_modules/travelQuotesService/normalizeTravelParams';
import { TravelCompareQuotesParams } from '../../../../../app/[site]/[locale]/(cms)/_modules/travelQuotesService/types';
import Translations from './i18n';
import type { DestinationOption } from '$/components/CountryChipPicker/types';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

const MAX_VISIBLE_COUNTRIES = 2;
const ANNUAL_TRIP_DURATION_DAYS = 365;

export interface TIOptionsValues {
  availableTripTypes: TravelInsuranceType[];
  countryOptions: DestinationOption[];
  countryMap: Map<string, string>;
  regionMap: Map<string, string>;
  // Compact view display values
  tripTypeLabel: string;
  destinationLabel: string;
  dateLabel: string;
  travelersLabel: string;
  // Boundaries
  minDate: Date;
  maxDate?: Date;
  maxDuration?: number;
  minAdults: number;
  maxAdults?: number;
  minChildren: number;
  maxChildren?: number;
  // handlers
  normalizeParams: (
    value: TravelCompareQuotesParams,
    options?: { skipDestination?: boolean },
  ) => TravelCompareQuotesParams;
}

export const useTIOptions = (
  searchParams: TravelCompareQuotesParams,
  countries: CmsCountry[],
  regions: CmsRegion[],
  boundaries: ComponentTravelPolicySearch,
  tripTypes: TravelInsuranceType[] | undefined,
): TIOptionsValues => {
  const context = useClientContext();
  const staticText = getClientTranslations(Translations, context);

  const countryOptions = useMemo<DestinationOption[]>(() => {
    let options = countries.map((country) => ({
      value: country.iso,
      label: country.label,
    }));

    if (boundaries.excludeDestinations?.length) {
      options = options.filter(
        (x) =>
          !boundaries.excludeDestinations?.some((ex) => ex.iso === x.value),
      );
    }

    if (boundaries.popularDestinations?.length) {
      options = options.sort((a, b) => {
        const aIndex = boundaries.popularDestinations!.findIndex(
          (pd) => pd.iso === a.value,
        );
        const bIndex = boundaries.popularDestinations!.findIndex(
          (pd) => pd.iso === b.value,
        );

        if (aIndex === -1 && bIndex === -1) {
          return a.label.localeCompare(b.label);
        }

        return bIndex - aIndex;
      });
    }

    return options;
  }, [countries, boundaries]);

  const countryMap = useMemo(() => {
    const map = new Map<string, string>();
    countries.forEach((country) => {
      map.set(country.iso, country.label);
    });
    return map;
  }, [countries]);

  const regionMap = useMemo(() => {
    const map = new Map<string, string>();
    regions.forEach((region) => {
      map.set(region.value, region.label);
    });
    return map;
  }, [regions]);

  const isSingleTrip = searchParams.insuranceType === 'SINGLE_PLAN';

  // Compact view display values
  const tripTypeLabel = isSingleTrip
    ? staticText.SINGLE_PLAN
    : staticText.ANNUAL_PLAN;

  const availableTripTypes: TravelInsuranceType[] = useMemo(
    () => tripTypes ?? ['SINGLE_PLAN', 'ANNUAL_PLAN'],
    [tripTypes],
  );

  const destinationLabel = useMemo(() => {
    if (isSingleTrip) {
      if (!searchParams.destinationIsoCodes?.length) {
        return '';
      }

      const countries = searchParams.destinationIsoCodes
        .slice(0, MAX_VISIBLE_COUNTRIES)
        .map((code) => countryMap.get(code) || code)
        .join(', ');
      const remaining = searchParams.destinationIsoCodes.length - 2;

      return remaining > 0 ? `${countries} +${remaining}` : countries;
    }

    return (
      regionMap.get(searchParams.region || '') || searchParams.region || ''
    );
  }, [isSingleTrip, searchParams, countryMap, regionMap]);

  const dateLabel = useMemo(() => {
    if (!searchParams.startDate) {
      return '';
    }

    const start = new Date(searchParams.startDate);
    const end = searchParams.endDate ? new Date(searchParams.endDate) : null;
    return formatTravelDateRangeLabel(start, end, context.lang);
  }, [searchParams.startDate, searchParams.endDate, context.lang]);

  let travelersLabel = `${searchParams.noOfAdults || 1}`;
  if (searchParams.noOfChildren) {
    travelersLabel += `+${searchParams.noOfChildren}`;
  }

  const minDate = new Date();
  const maxDuration = isSingleTrip
    ? boundaries.maxTripDuration
    : ANNUAL_TRIP_DURATION_DAYS;
  // != null: an advance window of 0 is a real bound (only today is a valid
  // start date), not "no limit".
  const maxDate =
    boundaries.advancePurchaseDuration != null
      ? addDays(new Date(), boundaries.advancePurchaseDuration)
      : undefined;
  const minAdults = boundaries.minAdult ?? MIN_ADULTS;
  const maxAdults = boundaries.maxAdult ?? MAX_TRAVELLERS;
  const minChildren = boundaries.minChildren ?? MIN_CHILDREN;
  const maxChildren = boundaries.maxChildren ?? MAX_TRAVELLERS;

  const normalizeParams = (
    value: TravelCompareQuotesParams,
    options?: { skipDestination?: boolean },
  ): TravelCompareQuotesParams => {
    const normalized = normalizeTravelParams(value, boundaries, context.brand);

    // Keep the raw destination for single-trip so a deliberately cleared
    // destination isn't replaced by the default fallback.
    if (
      options?.skipDestination &&
      normalized.insuranceType === 'SINGLE_PLAN'
    ) {
      return { ...normalized, destinationIsoCodes: value.destinationIsoCodes };
    }

    return normalized;
  };

  return {
    availableTripTypes,
    countryOptions,
    countryMap,
    regionMap,
    tripTypeLabel,
    destinationLabel,
    dateLabel,
    travelersLabel,
    minDate,
    maxDate,
    maxDuration,
    minAdults,
    maxAdults,
    minChildren,
    maxChildren,
    normalizeParams,
  };
};
